/**
 * PostOpCourseForm Component
 * 
 * Structured entry for post-operative day-by-day course.
 * Critical for UHC and site-of-care dispute appeals.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export interface PostOpDay {
  day: string; // 'POD 0', 'POD 1', etc.
  notes: string;
}

interface PostOpCourseFormProps {
  value: PostOpDay[];
  onChange: (days: PostOpDay[]) => void;
  disabled?: boolean;
}

const POD_OPTIONS = [
  'POD 0',
  'POD 1',
  'POD 2',
  'POD 3',
  'POD 4',
  'POD 5',
  'POD 6',
  'POD 7+',
];

const POD_SUGGESTIONS: Record<string, string[]> = {
  'POD 0': [
    'Multimodal pain management',
    'Post-op IV antibiotics',
    'PT consult ordered',
    'Foley catheter',
    'Sequential compression devices',
    'NPO/clear liquids',
  ],
  'POD 1': [
    'Ambulating with PT',
    'Oxygen via nasal canula',
    'JP drain output monitoring',
    'Diet advanced as tolerated',
    'Pain controlled with oral medications',
    'Incentive spirometry',
  ],
  'POD 2': [
    'Discharge criteria reviewed',
    'JP drain removal/maintained',
    'Independent ambulation',
    'Tolerating regular diet',
    'Home medications initiated',
    'Discharge teaching completed',
  ],
  'POD 3': [
    'Continued monitoring for complications',
    'Physical therapy progression',
    'Wound care education',
    'Follow-up appointments scheduled',
  ],
};

export const PostOpCourseForm: React.FC<PostOpCourseFormProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [newDay, setNewDay] = useState<string>('');

  const handleAddDay = () => {
    if (!newDay) return;
    
    // Check if day already exists
    if (value.some(d => d.day === newDay)) {
      return;
    }

    const updatedDays = [...value, { day: newDay, notes: '' }];
    // Sort by POD number
    updatedDays.sort((a, b) => {
      const aNum = parseInt(a.day.replace(/\D/g, ''), 10) || 0;
      const bNum = parseInt(b.day.replace(/\D/g, ''), 10) || 0;
      return aNum - bNum;
    });
    
    onChange(updatedDays);
    setNewDay('');
  };

  const handleUpdateNotes = (day: string, notes: string) => {
    onChange(
      value.map(d => (d.day === day ? { ...d, notes } : d))
    );
  };

  const handleRemoveDay = (day: string) => {
    onChange(value.filter(d => d.day !== day));
  };

  const handleAddSuggestion = (day: string, suggestion: string) => {
    const existing = value.find(d => d.day === day);
    if (!existing) return;
    
    const currentNotes = existing.notes;
    const newNotes = currentNotes 
      ? `${currentNotes}; ${suggestion}`
      : suggestion;
    
    handleUpdateNotes(day, newNotes);
  };

  const availableDays = POD_OPTIONS.filter(
    day => !value.some(d => d.day === day)
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Post-Operative Course
        </Typography>
        <Tooltip title="Document day-by-day clinical needs that justified the site of care. This is critical for inpatient/site-of-care dispute appeals, especially with UHC.">
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Stack>

      {/* Existing entries */}
      <Stack spacing={2}>
        {value.map((pod) => (
          <Paper
            key={pod.day}
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Chip
                label={pod.day}
                color="primary"
                size="small"
                sx={{ minWidth: 70, fontWeight: 600 }}
              />
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={`Enter clinical notes for ${pod.day}...`}
                  value={pod.notes}
                  onChange={(e) => handleUpdateNotes(pod.day, e.target.value)}
                  disabled={disabled}
                  size="small"
                  sx={{ mb: 1 }}
                />
                {POD_SUGGESTIONS[pod.day] && (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                      Quick add:
                    </Typography>
                    {POD_SUGGESTIONS[pod.day].slice(0, 4).map((suggestion) => (
                      <Chip
                        key={suggestion}
                        label={suggestion}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddSuggestion(pod.day, suggestion)}
                        disabled={disabled}
                        sx={{ 
                          mr: 0.5, 
                          mb: 0.5, 
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemoveDay(pod.day)}
                disabled={disabled}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Add new day */}
      {availableDays.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Add Day</InputLabel>
            <Select
              value={newDay}
              label="Add Day"
              onChange={(e) => setNewDay(e.target.value)}
              disabled={disabled}
            >
              {availableDays.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddDay}
            disabled={disabled || !newDay}
            size="small"
          >
            Add
          </Button>
        </Stack>
      )}

      {value.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
          No post-operative days added. Click "Add Day" to document the post-op course.
        </Typography>
      )}
    </Box>
  );
};
